import React from 'react';
import { Plane, FileText, MapPin, Luggage, CreditCard, HeartPulse } from 'lucide-react';
import PageHero, { Section, InfoCard, CTABlock } from '../components/PageHero';
import RussiaMap from '../components/RussiaMap';

const routes = [
  { from: 'Delhi (DEL)', to: 'Moscow (SVO/DME)', hours: '6–7 hrs', notes: 'Direct via Aeroflot (seasonal), or 1-stop via Doha, Dubai, Istanbul.' },
  { from: 'Mumbai (BOM)', to: 'Moscow (SVO/DME)', hours: '7–8 hrs', notes: '1-stop via Dubai, Doha, Istanbul is most economical.' },
  { from: 'Chennai (MAA)', to: 'Moscow (SVO)', hours: '8–10 hrs', notes: '1-stop via Dubai, Doha or Kuala Lumpur.' },
  { from: 'Kolkata (CCU)', to: 'Moscow (SVO)', hours: '9–11 hrs', notes: '1-stop via Doha, Dubai, Istanbul.' },
];

const docs = [
  { icon: FileText, title: 'Documents to Carry', desc: 'Original passport, student visa, invitation letter, admission letter, apostilled 10th/12th mark sheets, birth certificate, medical certificate, NEET scorecard.' },
  { icon: Luggage, title: 'Luggage Guide', desc: 'Most airlines allow 25–30 kg check-in for international students. Pack Indian snacks, spices, warm inners, formal wear & study essentials.' },
  { icon: CreditCard, title: 'Currency & Cash', desc: 'Carry USD 500–1000 in cash for the first month. Open a Russian bank account in the first 2 weeks with university help.' },
  { icon: HeartPulse, title: 'Health & Insurance', desc: 'Mandatory Russian medical insurance (arranged by university, ~$150/year). Carry your Indian prescription meds with a doctor’s note.' },
];

export default function Travel() {
  return (
    <>
      <PageHero eyebrow="India → Russia" title="Travel &" highlight="Arrival Guide"
        subtitle="Everything you need to know about flying to Russia — flight routes, documents, luggage, currency and arrival procedures."
        breadcrumbs={[{ label: 'India → Russia Travel' }]} />

      <Section eyebrow="Flight Routes" title="Popular India" highlight="→ Russia Routes">
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-primary text-white">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">From</th>
                <th className="text-left px-4 py-3 font-semibold">To</th>
                <th className="text-left px-4 py-3 font-semibold">Duration</th>
                <th className="text-left px-4 py-3 font-semibold">Notes</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-muted/40'}>
                  <td className="px-4 py-3 font-semibold text-primary">{r.from}</td>
                  <td className="px-4 py-3 font-semibold text-primary">{r.to}</td>
                  <td className="px-4 py-3 text-brand font-bold">{r.hours}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section eyebrow="Travel in Russia" title="Cities on the" highlight="Russian Education Network" muted>
        <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-3xl mb-6">
          Moscow is the primary international gateway. Every partner university city is well connected
          to Moscow via daily domestic flights, high-speed trains (Sapsan) and long-haul rail — most students
          fly Delhi/Mumbai → Moscow, then a short 1–4 hr domestic hop to their university city.
        </p>
        <RussiaMap />
      </Section>

      <Section eyebrow="What to Prepare" title="Before You" highlight="Board the Flight">
        <div className="grid sm:grid-cols-2 gap-5">
          {docs.map((d, i) => <InfoCard key={i} icon={d.icon} title={d.title}>{d.desc}</InfoCard>)}
        </div>
      </Section>

      <CTABlock title="First trip abroad? We handle everything." subtitle="From your visa stamping to airport pickup in Russia — our team is with you at every step." />
    </>
  );
}
